import { rem, transparentize } from 'polished';
import React, { useState, useEffect } from 'react';
import styled, { ThemeProvider } from 'styled-components';

import { User, Viewer } from '@vimeo/viewer';

import { Locales } from '../setLanguage';
import { EssentialFooterMenu } from './essential/EssentialFooterMenu';
import { EssentialLegalMenu } from './essential/EssentialLegalMenu';
import { EssentialQuotaMenu } from './essential/EssentialQuotaMenu';
import { applyCustomThemeEssentialFooter } from './essential/theme';
import { ThemeSwitcher } from './ThemeSwitcher';

const Footer = styled.div<{ position: string }>`
  background: ${({ theme }) =>
    `linear-gradient(${transparentize(1, theme.content.background)}, ${theme.content.background})`};
  display: flex;
  position: ${({ position }) => position};
  bottom: 0;
  right: 0;
  width: 100%;
  height: ${rem(50)};
  padding: 0 ${rem(16)};
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: center;
  /* allows users to click through the transparent footer, while keeping elements in the footer clickable. */
  pointer-events: none;
  transition: all 0.3s;
  > * {
    pointer-events: auto;
  }
`;

const Section = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

type Filters = Record<string, { rating: number; name: string }>;

interface Props {
  className?: string;
  position?: 'fixed' | 'absolute';
  contentViewingPrefs: {
    selected: number;
    filters: Filters;
    customFilters: Filters;
  };
  impressumQualifies?: boolean;
  locale: string;
  locales?: Locales[];
  localeLabels?: string[];
  xsrft: string;
  user?: User | null;
  jwt?: string;
  apiUrl?: string;
  teamOwnersId?: number | null;
  enableQuotaMenu?: boolean;
  terminateContractQualifies?: boolean;
}

function EssentialFooter({
  className = '',
  position = 'fixed',
  contentViewingPrefs,
  impressumQualifies,
  locale,
  locales = [],
  localeLabels = [],
  xsrft,
  user,
  jwt,
  apiUrl,
  teamOwnersId,
  enableQuotaMenu = true,
  terminateContractQualifies = false,
}: Props) {
  const [isDoNotSellReady, setIsDoNotSellReady] = useState(false);

  useEffect(() => {
    window.onload = () => setIsDoNotSellReady(true);
  }, []);

  return (
    <ThemeProvider theme={applyCustomThemeEssentialFooter}>
      <Footer className={className} position={position}>
        <Section>
          <EssentialQuotaMenu
            user={user}
            jwt={jwt}
            apiUrl={apiUrl}
            teamOwnersId={teamOwnersId}
            enableQuotaMenu={enableQuotaMenu}
          />
        </Section>
        <Section>
          <EssentialLegalMenu
            // Don't show the two popover menus at the same time
            impressumQualifies={impressumQualifies}
            terminateContractQualifies={terminateContractQualifies}
            isDoNotSellReady={isDoNotSellReady}
          />
          <ThemeSwitcher />

          <EssentialFooterMenu
            impressumQualifies={impressumQualifies}
            locale={locale}
            locales={locales}
            localeLabels={localeLabels}
            token={xsrft}
            isDoNotSellReady={isDoNotSellReady}
            contentViewingPrefs={contentViewingPrefs as Viewer['contentViewingPrefs']}
          />
        </Section>
      </Footer>
    </ThemeProvider>
  );
}

export default EssentialFooter;
